import type { CarouselProps } from "components/HomeCarousel/HomeCarousel"
import { DeprecatedBandCButton } from "packages/basket-and-checkout/shared/components/Button/Button"
import { CarouselModule } from "packages/ui-shared/components/CarouselModule"
import React from "react"
import type {
  RecommendationItem,
  RecommendationSection,
  Tracking,
  Maybe,
  ProductCardProduct,
} from "types"
import { BasketSectionsHeader } from "../../../basket-and-checkout/shared/components/BasketSectionsHeader/BasketSectionsHeader"
import { inertSectionWrapper } from "../../../basket-and-checkout/shared/components/BasketSectionsHeader/BasketSectionsHeader.css"
import { Product } from "./Product"

import {
  footerCTA,
  productCardWrapperContainer,
  recommendationContainer,
} from "./RecommendationModule.css"
import { toHtmlId } from "./utils/toHtmlId"
import { trackClicked } from "tracking/utils/trackClicked"

const RecommendationCarousel = ({
  items,
  tracking,
  carouselPropsOverride = {},
  carouselWrapperClassName = "",
  onAddToBag,
  locationType,
}: {
  items: RecommendationItem[]
  tracking: Maybe<Tracking> | undefined
  carouselPropsOverride?: CarouselProps
  carouselWrapperClassName?: string
  onAddToBag?: (product: ProductCardProduct) => void
  locationType: string
}) => {
  return (
    <CarouselModule
      carouselWrapperClassName={`${productCardWrapperContainer} ${carouselWrapperClassName}`}
      tracking={tracking}
      carouselProps={carouselPropsOverride}
    >
      {items.map((item) => {
        return (
          <Product
            onAddToBag={onAddToBag}
            key={item.id}
            item={item}
            locationType={locationType}
          />
        )
      })}
    </CarouselModule>
  )
}

const componentTypeMap = {
  CAROUSEL: RecommendationCarousel,
}

export const RecommendationModule = ({
  id,
  componentType,
  title,
  description,
  items,
  tracking,
  carouselPropsOverride = {},
  carouselWrapperClassName = "",
  locationType,
  onAddToBag,
  cta,
  onCtaClick,
}: RecommendationSection & {
  carouselWrapperClassName?: string
  carouselPropsOverride?: CarouselProps
  onAddToBag?: (product: ProductCardProduct) => void
  onCtaClick?: () => void
}) => {
  const Component = componentTypeMap[componentType]

  if (!Component) return null

  return (
    <div
      id={toHtmlId(id)}
      data-testid="recommendation-section"
      className={recommendationContainer}
    >
      <BasketSectionsHeader
        title={title || ""}
        description={description || ""}
        {...(cta?.type === "header" ? { cta } : {})}
        {...(onCtaClick ? { onCtaClick } : {})}
      />
      <div className={inertSectionWrapper}>
        <Component
          items={items}
          tracking={tracking}
          onAddToBag={onAddToBag}
          locationType={locationType}
          {...(componentType === "CAROUSEL"
            ? {
                carouselPropsOverride: carouselPropsOverride,
                carouselWrapperClassName,
              }
            : {})}
        />
      </div>
      {cta?.type === "footer" && (
        <DeprecatedBandCButton
          className={footerCTA}
          as="a"
          variant="secondary"
          href={cta.link as string}
          onClick={() => {
            if (cta.tracking?.clicked) {
              trackClicked(cta.tracking.clicked)
            }
          }}
        >
          {cta.text}
        </DeprecatedBandCButton>
      )}
    </div>
  )
}
